import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { func, object, string } from 'prop-types';
import { OutsideClickHandler, ResponsiveImage } from '../../../components';
import TransactionDetail from './TransactionDetail';

import { useIntl } from 'react-intl';

import get from 'lodash/get';
import { ensureTransaction } from '../../../util/data';
import { formatMoney } from '../../../util/currency';
import { formatTransitionName } from '../../../util/transaction';
import { useLocation } from 'react-router-dom';
import { parse } from '../../../util/urlHelpers';

import css from './TransactionItem.css';

const AVATAR_IMAGE_VARIANTS = [
  // 40x40
  'square-xsmall',

  // 80x80
  'square-xsmall2x',

  // 240x240
  'square-small',

  // 480x480
  'square-small2x',
];

const TransactionItem = ({ className, item, isOpenDetail, onClick = () => null }) => {
  const intl = useIntl();
  const transaction = ensureTransaction(item);
  const { id, attributes, provider, customer, listing } = transaction;
  const { lastTransition, createdAt, lastTransitionedAt, processName } = attributes;
  const isMainRental = processName === 'main-rental';
  const formattedTransitionName = formatTransitionName(lastTransition);
  const formattedCreatedAt = moment(createdAt).format('DD/MM/YYYY');
  const formattedLastTransitionedAt = moment(lastTransitionedAt).format('DD/MM/YYYY');
  const { title: lTitle, price: lPrice } = get(listing, 'attributes');
  const formattedPrice = formatMoney(intl, lPrice);
  const { displayName: cDisplayName } = get(customer, 'attributes.profile');

  const handleBlur = useCallback(() => {
    if (!isOpenDetail) {
      return;
    } else {
      onClick(id.uuid);
    }
  }, [isOpenDetail, id.uuid]);

  const handleFocus = useCallback(() => {
    onClick(isOpenDetail ? null : id.uuid);
  }, [isOpenDetail, id.uuid]);

  const classes = classNames(css.root, className, {
    [css.openDetail]: isOpenDetail,
    [css.mainRentalItem]: isMainRental,
  });
  const rootContainerClasses = classNames(css.rootContainer, {
    [css.rootContainerExpand]: isOpenDetail,
  });

  return (
    <OutsideClickHandler className={rootContainerClasses} onOutsideClick={handleBlur}>
      <div className={classes} onClick={handleFocus}>
        <div>
          {listing.images[0] ? (
            <ResponsiveImage
              rootClassName={css.thumbnailImage}
              alt={`transaction ${id.uuid}`}
              image={listing.images[0]}
              variants={AVATAR_IMAGE_VARIANTS}
              sizes={'36px'}
            />
          ) : (
            <div className={css.thumbnailImagePlaceholder}></div>
          )}
        </div>
        <h4 className={css.listingTitle}>
          {isMainRental ? (
            <span className={css.mainRentalType}>
              {intl.formatMessage({
                id: 'TransactionItem.mainRental',
                defaultMessage: 'Main rental',
              })}
            </span>
          ) : (
            <span className={css.extendRentalType}>
              {intl.formatMessage({
                id: 'TransactionItem.extendRental',
                defaultMessage: 'Extend rental',
              })}
            </span>
          )}
          <span className={css.separatedDot}>•</span>
          {lTitle}
          <span className={css.separatedDot}>•</span>
          {cDisplayName}
          <span className={css.separatedDot}>•</span>
          {formattedPrice}
        </h4>
        <div className={css.headerInfo}>
          <span className={css.lastTransition}>{formattedTransitionName}</span>
          <span className={css.timeItem}>{formattedCreatedAt}</span>
          <span className={css.timeItem}>{formattedLastTransitionedAt}</span>
        </div>
      </div>
      {isOpenDetail && <TransactionDetail item={item} />}
    </OutsideClickHandler>
  );
};

TransactionItem.propTypes = {
  className: string,
  item: object,
  onClick: func,
};

export default TransactionItem;
